import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { useNavigate } from '@remix-run/react';
import { ModalElement } from '@shared/dream-components';

import Button from '~/components/Button';
import useIsLoggedIn from '~/hooks/useIsLoggedIn';

interface Props {
  isUpgradeActive: boolean;
  onContinue?: () => void;
  onClose?: () => void;
  subscriberId?: string;
}

const PremiumMemberModal = ({ isUpgradeActive, onContinue, onClose, subscriberId }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isLoggedIn = useIsLoggedIn();
  const [modalOpen, setModalOpen] = useState(isUpgradeActive);
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    setModalOpen(isUpgradeActive);
  }, [isUpgradeActive]);

  const handleClose = () => {
    setModalOpen(false);
    onClose?.();
  };

  const handleContinue = () => {
    setModalOpen(false);
    onContinue?.();
  };

  const handleManageSubscriptions = () => {
    setModalOpen(false);
    navigate(`/subscribe/${subscriberId}/manage`);
  };

  const modal = (
    <ModalElement
      element={{ type: 'modal' }}
      isOpen={modalOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      isLoading={false}
      showCloseButton
    >
      <div className="bg-white p-6 md:w-[448px]">
        <h3 className="pb-1 pr-8 text-xl font-bold leading-tight tracking-medium text-gray-900">
          {t('stripe.upgrade_warning.modal.title')}
        </h3>
        <p className="mb-4 pb-1 text-sm font-light leading-tight tracking-wider text-gray-500">
          {t('stripe.upgrade_warning.modal.message')}
        </p>

        <div className="flex w-full justify-end gap-2">
          {!isLoggedIn && <Button onClick={() => navigate('/login')}>{t('shared.cta.login')}</Button>}
          {isLoggedIn && subscriberId && (
            <Button onClick={handleManageSubscriptions} variant="secondary">
              {t('nav.links.manage')}
            </Button>
          )}
          {onContinue && (
            <Button onClick={handleContinue} variant="primary">
              {t('shared.cta.continue')}
            </Button>
          )}
        </div>
      </div>
    </ModalElement>
  );

  return mounted ? createPortal(modal, document.body) : null;
};

export default PremiumMemberModal;
